import { HealthInsurerDetail } from '@/types/HealthInsurerDetail'
import { DetailSection } from '@/components/detail/DetailSection'
import { FieldGrid, FieldItem } from '@/components/detail/FieldGrid'
import { css, useTheme } from '@emotion/react'
import { compareDateYmd } from '@/utils/dateUtils'

const appliedDateContainerStyle = css`
  margin-bottom: 32px;
`

// 2カラムレイアウト（保険者番号+保険者管理枝番、電話番号+FAX番号）
const twoColumnRowStyle = css`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-top: 8px;
`

const fieldColumnStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
`

type Props = {
  data: HealthInsurerDetail
}

export const HealthInsurerView = ({ data }: Props) => {
  const theme = useTheme()

  const getStartDateIndicator = () => {
    if (compareDateYmd(data.startDate, data.startDateCurr ?? new Date()) === 0) {
      return { type: 'dotAndText' as const, color: theme.c.bc.active, text: '現在適用中' }
    }
    if (data.startDatePending && compareDateYmd(data.startDate, data.startDatePending) === 0) {
      return { type: 'dotAndText' as const, color: theme.c.bc.inactive, text: '適用待ち' }
    }
    if (compareDateYmd(data.startDate, data.startDateCurr ?? new Date()) < 0) {
      return { type: 'dotAndText' as const, color: theme.c.bc.inactive, text: '適用終了' }
    }
    return undefined
  }

  return (
    <>
      <div css={appliedDateContainerStyle}>
        <FieldItem
          label="適用開始日"
          value={new Intl.DateTimeFormat('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(new Date(data.startDate))}
          indicator={getStartDateIndicator()}
        />
      </div>
      <DetailSection number={1} title="基本情報" isFirst={true}>
        <FieldGrid>
          {/* 保険者番号と保険者管理枝番を横並び */}
          <div css={twoColumnRowStyle}>
            <div css={fieldColumnStyle}>
              <FieldItem label="保険者番号" value={data.insurerCode} />
            </div>
            <div css={fieldColumnStyle}>
              <FieldItem label="保険者管理枝番" value={data.branchCode || ''} />
            </div>
          </div>
          <FieldItem
            label="代表保険者番号"
            value={data.primaryInsurerCode || ''}
          />
          <FieldItem
            label="代表保険者番号（鍼灸）"
            value={data.primaryInsurerCodeAcumoxi || ''}
          />
          <FieldItem label="保険者名" value={data.insurerName} />
          <FieldItem label="保険種別" value={data.insuranceTypeName} />
          <FieldItem label="都道府県" value={data.prefectureName} />
        </FieldGrid>
      </DetailSection>
      <DetailSection number={2} title="住所・連絡先">
        <FieldGrid>
          <FieldItem label="郵便番号" value={data.postalCode} />
          <FieldItem label="住所1" value={data.addressLine1} />
          <FieldItem label="住所2" value={data.addressLine2 || ''} />
          {/* 電話番号とFAX番号を横並び */}
          <div css={twoColumnRowStyle}>
            <div css={fieldColumnStyle}>
              <FieldItem label="電話番号" value={data.phoneNumber || ''} />
            </div>
            <div css={fieldColumnStyle}>
              <FieldItem label="FAX番号" value={data.faxNumber || ''} />
            </div>
          </div>
        </FieldGrid>
      </DetailSection>
      <DetailSection number={3} title="送達先情報">
        <FieldGrid>
          <FieldItem label="敬称" value={data.honorificName ?? ''} />
          <FieldItem
            label="保険者別送達先名"
            value={data.insurerDestinationName || ''}
          />
          <FieldItem label="送達先名" value={data.destinationName || ''} />
          <FieldItem
            label="保険者別送達先名（鍼灸）"
            value={data.insurerDestinationNameAcumoxi || ''}
          />
          <FieldItem
            label="送達先名（鍼灸）"
            value={data.destinationNameAcumoxi || ''}
          />
          <FieldItem
            label="送達先名（受領委任）"
            value={data.delegatedDestinationName || ''}
          />
        </FieldGrid>
      </DetailSection>
    </>
  )
}
